<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeMount, onMounted, provide, ref, watch } from "vue";
import { APP, useAppStore } from "@/stores/app";
import { JETSTREAM, useJetstreamStore } from "./stores/jetstream";
import { type DeckFeatureFlags, type DeckProperties } from "@/types";
import { parseAttributeAsBoolean } from "@/utils/attribute";
import ErrorBoundary from "@/components/ErrorBoundary.vue";
import TemplateLoader from "@/templates/TemplateLoader.vue";
import StreamContainer from "@/components/StreamContainer.vue";
import { LOGGER, useLogger } from "@/stores/logger";
import LoadingScreen from "@/components/LoadingScreen.vue";
import FullScreen from "@/components/FullScreen.vue";
import { CONNECTION, useConnectionStore } from "@/stores/connection";
import { CONFIG, useConfigStore } from "@/stores/config";

// define props
const props = defineProps<DeckProperties>();

// initialize stores
const log = useLogger();
const appStore = useAppStore(log);
const configStore = useConfigStore();
const connectionStore = useConnectionStore(appStore, configStore, log);
const jetStreamStore = useJetstreamStore(appStore, connectionStore, configStore, log);

// provide stores to child component instances
provide(LOGGER, log);
provide(APP, appStore);
provide(CONFIG, configStore);
provide(CONNECTION, connectionStore);
provide(JETSTREAM, jetStreamStore);

const appRef = ref<HTMLElement>();
const readyToShowUi = computed(
  () => configStore.configLoaded && (parseAttributeAsBoolean(props.active) || props.dev)
);

onBeforeMount(async () => {
  log.debug("Initializing new app instance");
  appStore.dev = props.dev;
  connectionStore.sessionId = props.sessionId;
  appStore.active = parseAttributeAsBoolean(props.active);
  appStore.prStringHtmlAttribute = props.prString;
  appStore.cameraHtmlAttribute = props.camera;
  appStore.cameraFlightHtmlAttribute = props.cameraFlight;
  appStore.multisessionDisconnectHtmlAttribute = parseAttributeAsBoolean(
    props.multisessionDisconnect
  );
  appStore.hideConsumptionDataHtmlAttribute = parseAttributeAsBoolean(props.hideConsumptionData);
  appStore.consumptionDataHtmlAttribute = props.consumptionData;

  if (props.configId) {
    await configStore.initializeConfig(props);
  }
});

onMounted(() => {
  appStore.instance = appRef.value;
});

watch(
  () => props.configId,
  async (newValue) => {
    // Note: "configID" is not supposed to be reactive, since it is only used during the initial connect
    // Still, having a listener here will add some leniency for early re-renders within integrating frameworks
    log.debug("Attribute 'configId' changed to", newValue);
    if (appStore.active) {
      log.warn("Changing 'configId' when already active will have no effect.");
    }

    await configStore.initializeConfig(props);
  }
);

watch(
  () => props.streamingStage,
  (newStreamingStage) => {
    // Note: "streamingStage" is not supposed to be reactive, since it is only used during the initial connect
    // Still, having a listener here will add some leniency for early re-renders within integrating frameworks
    log.debug("Attribute 'streamingStage' changed to", newStreamingStage);
    if (connectionStore.connected) {
      log.warn(
        "Cannot change 'streamingStage' when already connected. The change will have no effect."
      );
      return;
    }
    configStore.updateConfig({ streamingStage: newStreamingStage });
  }
);

watch(
  () => props.active,
  (newValue) => {
    const active = parseAttributeAsBoolean(newValue);
    log.debug("Attribute 'active' changed to", active);
    connectionStore.sessionId = props.sessionId;
    appStore.active = active;
  }
);

watch(
  () => props.sessionId,
  (newSessionId) => {
    // Note: "sessionId" is not supposed to be reactive, since it is only used during the initial connect
    // Still, having a listener here will add some leniency for early re-renders within integrating frameworks
    log.debug("Attribute 'sessionId' changed to", newSessionId);
    if (connectionStore.connected) {
      log.warn("Changing 'sessionId' when already connected will not affect the current session.");
    }
    connectionStore.sessionId = newSessionId;
  }
);

watch(
  () => props.prString,
  (prString) => {
    log.debug("Attribute 'prString' changed to", prString);
    appStore.prStringHtmlAttribute = prString;
  }
);

watch(
  () => props.camera,
  (newCamera) => {
    // TODO emit camera when changed?
    log.debug("Attribute 'camera' changed to", newCamera);
    appStore.cameraHtmlAttribute = newCamera;
  }
);

watch(
  () => props.cameraFlight,
  (newValue) => {
    log.debug("Attribute 'cameraFlight' changed to", newValue);
    appStore.cameraFlightHtmlAttribute = newValue;
  }
);

watch(
  () => props.market,
  (newMarket) => {
    // Note: "market" is not supposed to be reactive, since it is only used during the initial connect.
    // Still, having a listener here will add some leniency for early re-renders within integrating frameworks
    log.debug("Attribute 'market' changed to", newMarket);
    if (connectionStore.connected) {
      log.warn("Cannot change 'market' when already connected. The change will have no effect.");
      return;
    }
    configStore.updateConfig({ market: newMarket });
  }
);

watch(
  () => props.features,
  (newFeatures) => {
    log.debug("Attribute 'features' changed to", newFeatures);
    if (!newFeatures || newFeatures === JSON.stringify(configStore.config?.features)) {
      log.debug("Feature flags already in effect, no need to change anything");
      return;
    }

    let features: DeckFeatureFlags = { ...(configStore.config?.features || {}) };

    try {
      features = { ...features, ...JSON.parse(newFeatures) };
    } catch (e) {
      log.error("Error parsing 'features' attribute", e);
      return;
    }

    configStore.updateConfig({ features });
    log.debug("New feature flags in effect", features);
    getCurrentInstance()?.proxy?.$forceUpdate();
  }
);

watch(
  () => props.multisessionDisconnect,
  (newValue) => {
    const multisessionDisconnect = parseAttributeAsBoolean(newValue);
    log.debug("Attribute 'multisession-disconnect' changed to", multisessionDisconnect);
    appStore.multisessionDisconnectHtmlAttribute = multisessionDisconnect;
  }
);

watch(
  () => props.consumptionData,
  (newValue) => {
    if (newValue) {
      log.debug("Attribute 'consumption-data' changed to", newValue);
      appStore.consumptionDataHtmlAttribute = newValue;
    }
  }
);

watch(
  () => props.hideConsumptionData,
  (newValue) => {
    log.debug("Attribute 'hide-consumption-data' changed to", newValue);
    appStore.hideConsumptionDataHtmlAttribute = parseAttributeAsBoolean(newValue);
  }
);
</script>

<template>
  <div
    class="deck-app"
    ref="appRef"
    :data-logid="log.logId"
    v-show="appStore.active || appStore.dev"
  >
    <FullScreen>
      <ErrorBoundary>
        <Transition name="fade">
          <LoadingScreen v-if="!jetStreamStore.streamInitialized" />
        </Transition>
        <StreamContainer v-if="readyToShowUi" />
        <Transition name="fade">
          <TemplateLoader v-if="readyToShowUi" />
        </Transition>
      </ErrorBoundary>
    </FullScreen>
  </div>
</template>

<style>
:host {
  display: block;
  height: 100%;
  width: 100%;
}

.deck-app {
  position: relative;
  height: inherit;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
